import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import mapboxgl, { Map } from 'mapbox-gl';
import MapboxCompare from 'mapbox-gl-compare';
import 'mapbox-gl/dist/mapbox-gl.css';
import 'mapbox-gl-compare/dist/mapbox-gl-compare.css';
import { Site } from '../../api/Api';
import MapboxFeature from '../../mapbox-helpers/MapboxFeature';
import { mapBoxConfig } from '../../mapbox-helpers/config';
import { bbox } from '@turf/turf';
import { CustomRasterLayer, MapLayerInteractiveElement } from '../../mapbox-helpers/map-types';
import { addSiteBoundary, fitMapToBoundaries, addSiteBoundaryMarker } from 'src/mapbox-helpers/map-layer-utils';
import { Box, CircularProgress, IconButton, Typography } from '@mui/material';
import CenterFocusWeakIcon from '@mui/icons-material/CenterFocusWeak';

if (mapBoxConfig.accessToken) {
  mapboxgl.accessToken = mapBoxConfig.accessToken;
} else {
  console.error('Mapbox access token is not configured in mapBoxConfig.');
}

interface SiteMapWithCompareProps {
  sites: Site[] | undefined;
  requiredBounrdaryMarker?: boolean;
  fitToBoundary?: boolean;
  isImageryLoading?: boolean;
}

const SiteMapWithCompare: React.FC<SiteMapWithCompareProps> = ({
  sites,
  requiredBounrdaryMarker,
  fitToBoundary,
  isImageryLoading,
}) => {
  const mapContainerBeforeRef = useRef<HTMLDivElement | null>(null);
  const mapContainerAfterRef = useRef<HTMLDivElement | null>(null);
  const compareContainerRef = useRef<HTMLDivElement | null>(null);
  const beforeMapRef = useRef<Map | null>(null);
  const afterMapRef = useRef<Map | null>(null);
  const mapboxCompareRef = useRef<InstanceType<typeof MapboxCompare> | null>(null);
  const lastViewRef = useRef<{
      center: [number, number];
      zoom: number;
      bearing: number;
      pitch: number;
    } | null>(null);

  const isFirstLoadRef = useRef(true);

  const [afterMapLayerToggle, setAfterMapLayerToggle] = useState<Array<MapLayerInteractiveElement> | undefined>();
  const [mapStyle, setMapStyle] = useState<string>(mapBoxConfig.mapStyle);
  const [tooltip, setTooltip] = useState<{ visible: boolean; content: string; x: number; y: number }>({ visible: false, content: '', x: 0, y: 0 });
  const [isSiteListExpanded, setIsSiteListExpanded] = useState(true);

  const processedYearTilesets = useMemo(() => {
    if (!sites || sites.length === 0) return {};
    const firstSiteWithImagery = sites.find(s => s.satellite_imagery && Array.isArray(s.satellite_imagery) && (s.satellite_imagery as unknown as CustomRasterLayer[]).length > 0);
    if (!firstSiteWithImagery || !firstSiteWithImagery.satellite_imagery) return {};

    const tilesets: Record<string, CustomRasterLayer> = {};
    const imageryList = firstSiteWithImagery.satellite_imagery as unknown as CustomRasterLayer[];
    imageryList.forEach(imagery => {
      if (imagery.year && imagery.url && imagery.type === 'SATELITE_IMAGE') {
        tilesets[imagery.year] = imagery;
      }
    });
    return tilesets;
  }, [sites]);

  const availableYearKeys = useMemo(() => Object.keys(processedYearTilesets).sort(), [processedYearTilesets]);

  const [currentBeforeMapKey, setCurrentBeforeMapKey] = useState<string>(
    availableYearKeys.length > 0 ? availableYearKeys[0] : ''
  );
  const [currentAfterMapKey, setCurrentAfterMapKey] = useState<string>(
    availableYearKeys.length > 0 ? availableYearKeys[0] : ''
  );

  useEffect(() => {
    if (availableYearKeys.length > 0) {
      if (!currentBeforeMapKey) setCurrentBeforeMapKey(availableYearKeys[0]);
      if (!currentAfterMapKey) setCurrentAfterMapKey(availableYearKeys[0]);
    }
  }, [availableYearKeys, currentBeforeMapKey, currentAfterMapKey]);

  const [currentBeforeYearRasterLayerIds, setCurrentBeforeYearRasterLayerIds] = useState<string[]>([]);
  const [currentAfterYearRasterLayerIds, setCurrentAfterYearRasterLayerIds] = useState<string[]>([]);
  const [isBeforeMapStyleLoaded, setIsBeforeMapStyleLoaded] = useState(false);
  const [isAfterMapStyleLoaded, setIsAfterMapStyleLoaded] = useState(false);

  const commonSiteBoundaryMarkersRef = useRef<mapboxgl.Marker[]>([]);

  const hasAnySiteWithYearlyImagery = useMemo(() => {
    if (!sites || sites.length === 0) return false;
    return sites.some(site => site.satellite_imagery && Array.isArray(site.satellite_imagery) && (site.satellite_imagery as Array<CustomRasterLayer>)?.length > 0);
  }, [sites]);

  const [showSatelliteImagery, setShowSatelliteImagery] = useState<boolean>(hasAnySiteWithYearlyImagery);

  React.useEffect(() => {
    if (!isImageryLoading) {
      setShowSatelliteImagery(true);
    }
  }, [isImageryLoading]);

  const addOrUpdateSiteYearRasterLayers = useCallback(
    async (
      mapInstance: Map | null,
      selectedYear: string,
      isVisible: boolean,
      currentLayerIdsState: string[],
      setCurrentLayerIdsState: React.Dispatch<React.SetStateAction<string[]>>,
      mapNamePrefix: 'before' | 'after'
    ) => {
      if (!mapInstance) return;

      currentLayerIdsState.forEach(layerId => {
        if (mapInstance.getLayer(layerId)) mapInstance.removeLayer(layerId);
        const sourceId = layerId.replace('-layer', '-source');
        if (mapInstance.getSource(sourceId)) mapInstance.removeSource(sourceId);
      });

      setCurrentLayerIdsState([]);

      if (!isVisible || !selectedYear || !sites || sites.length === 0) return;

      const newLayerIds: string[] = [];
      for (const site of sites) {
        if (site.satellite_imagery && Array.isArray(site.satellite_imagery)) {
          const imageryList = site.satellite_imagery as unknown as CustomRasterLayer[];
          const rasterData = imageryList.find(img => img.year === selectedYear && img.type === 'SATELITE_IMAGE');

          if (!rasterData || !rasterData.url) {
            console.warn(`Site ${site.site_id} does not have imagery for year ${selectedYear}.`);
            continue;
          }

          const sanitizedSiteId = site.site_id.replace(/\./g, '-');
          const sourceId = `${mapNamePrefix}-site-${sanitizedSiteId}-year-${selectedYear}-raster-${rasterData.id}-source`;
          const layerId = `${mapNamePrefix}-site-${sanitizedSiteId}-year-${selectedYear}-raster-${rasterData.id}-layer`;

          if (!mapInstance.getSource(sourceId)) {
            mapInstance.addSource(sourceId, {
              type: 'raster',
              tiles: [rasterData.url.split('?')[0]],
              tileSize: 256,
            } as mapboxgl.RasterSourceSpecification);
          }

          if (!mapInstance.getLayer(layerId)) {

            const fillLayerId = `fill-site-boundary-${site.site_id}`;
            let beforeId: string | undefined = undefined;

            if (mapInstance.getLayer(fillLayerId)) {
              // Insert raster below the site boundary fill layer
              beforeId = fillLayerId;
            } else {
              // Fallback: put it below the first symbol layer (labels)
              const style = mapInstance.getStyle();
              if (style && style.layers) {
                const firstSymbolLayer = style.layers.find(l => l.type === 'symbol');
                if (firstSymbolLayer) {
                  beforeId = firstSymbolLayer.id;
                }
              }
            }

            mapInstance.addLayer({
              id: layerId,
              type: 'raster',
              source: sourceId,
              paint: { 'raster-opacity': 1 },
            }, beforeId
            );
            newLayerIds.push(layerId);
          }
        } else {
          console.warn(`Site ${site.site_id} has no satellite_imagery defined or it's not an array.`);
        }
      }
      setCurrentLayerIdsState(newLayerIds);
    },
    [sites]
  );

  useEffect(() => {
    if (isBeforeMapStyleLoaded && beforeMapRef.current) {
      addOrUpdateSiteYearRasterLayers(
        beforeMapRef.current,
        currentBeforeMapKey,
        showSatelliteImagery,
        currentBeforeYearRasterLayerIds,
        setCurrentBeforeYearRasterLayerIds,
        'before'
      );
    }
  }, [isBeforeMapStyleLoaded, currentBeforeMapKey, addOrUpdateSiteYearRasterLayers, sites, showSatelliteImagery]);

  useEffect(() => {
    if (isAfterMapStyleLoaded && afterMapRef.current) {
      addOrUpdateSiteYearRasterLayers(
        afterMapRef.current,
        currentAfterMapKey,
        showSatelliteImagery,
        currentAfterYearRasterLayerIds,
        setCurrentAfterYearRasterLayerIds,
        'after'
      );
    }
  }, [isAfterMapStyleLoaded, currentAfterMapKey, addOrUpdateSiteYearRasterLayers, sites, showSatelliteImagery]);

  const initializeMaps = useCallback(() => {
    if (!mapContainerBeforeRef.current || !mapContainerAfterRef.current || !compareContainerRef.current) return;

    const defaultCenter: [number, number] = [
      mapBoxConfig.defaulView.lng,
      mapBoxConfig.defaulView.lat,
    ];
    const defaultZoom = mapBoxConfig.defaulView.zoom;

    const fallbackCenter =
      sites && sites.length > 0 && sites[0].centroid_wkt
        ? MapboxFeature.parsePointToArray(sites[0].centroid_wkt)
        : defaultCenter;
    const fallbackZoom =
      sites && sites.length > 0 && sites[0].boundary ? 10 : defaultZoom;

    const initialCenter = lastViewRef.current?.center ?? fallbackCenter;
    const initialZoom = lastViewRef.current?.zoom ?? fallbackZoom;
    const bearing = lastViewRef.current?.bearing ?? 0;
    const pitch = lastViewRef.current?.pitch ?? 0;

    setIsBeforeMapStyleLoaded(false);
    setIsAfterMapStyleLoaded(false);

    beforeMapRef.current = new mapboxgl.Map({
      container: mapContainerBeforeRef.current as HTMLDivElement,
      style: `mapbox://styles/mapbox/${mapStyle}`,
      center: initialCenter,
      zoom: initialZoom,
      bearing,
      pitch,
    });

    afterMapRef.current = new mapboxgl.Map({
      container: mapContainerAfterRef.current as HTMLDivElement,
      style: `mapbox://styles/mapbox/${mapStyle}`,
      center: initialCenter,
      zoom: initialZoom,
      bearing,
      pitch,
    });

    afterMapRef.current.addControl(new mapboxgl.NavigationControl({ showCompass: true }), 'bottom-right');
    if (compareContainerRef.current) {
      afterMapRef.current.addControl(new mapboxgl.FullscreenControl({ container: compareContainerRef.current }), 'bottom-right');
    } else {
      afterMapRef.current.addControl(new mapboxgl.FullscreenControl(), 'bottom-right');
    }

    if (mapboxCompareRef.current) mapboxCompareRef.current.remove();
    mapboxCompareRef.current = new MapboxCompare(
      beforeMapRef.current,
      afterMapRef.current,
      compareContainerRef.current as HTMLElement
    );

    beforeMapRef.current.on('style.load', () => setIsBeforeMapStyleLoaded(true));
    afterMapRef.current.on('style.load', () => setIsAfterMapStyleLoaded(true));

  }, [mapStyle, sites]);

  useEffect(() => {
    setAfterMapLayerToggle([]);
    setCurrentBeforeYearRasterLayerIds([]);
    setCurrentAfterYearRasterLayerIds([]);
    setTimeout(() => initializeMaps(), 500);

    const resizeHandler = () => {
      beforeMapRef.current?.resize();
      afterMapRef.current?.resize();
    };
    window.addEventListener('resize', resizeHandler);

    return () => {
      if (afterMapRef.current) {
        const center = afterMapRef.current.getCenter();
        const zoom = afterMapRef.current.getZoom();
        const bearing = afterMapRef.current.getBearing();
        const pitch = afterMapRef.current.getPitch();

        lastViewRef.current = {
          center: [center.lng, center.lat],
          zoom,
          bearing,
          pitch,
        };
      }

      mapboxCompareRef.current?.remove();
      mapboxCompareRef.current = null;

      if (beforeMapRef.current) {
        beforeMapRef.current.remove();
        beforeMapRef.current = null;
      }
      if (afterMapRef.current) {
        afterMapRef.current.remove();
        afterMapRef.current = null;
      }

      window.removeEventListener('resize', resizeHandler);
    };

  }, [mapStyle, sites, requiredBounrdaryMarker, fitToBoundary, initializeMaps]);

  const handleMouseEnter = (e: mapboxgl.MapMouseEvent & { features?: mapboxgl.MapboxGeoJSONFeature[] | undefined; }) => {
    if (!e.features?.length) return;
    const feature = e.features[0];
    if (feature.properties) {
      const content = `Name: ${feature.properties.Name}<br/>Area: ${feature.properties.Area}`;
      setTooltip({ visible: true, content, x: e.point.x, y: e.point.y });
      if (afterMapRef.current) afterMapRef.current.getCanvas().style.cursor = 'pointer';
    }
  };

  const handleMouseLeave = () => {
    setTooltip((prev) => ({ ...prev, visible: false }));
    if (afterMapRef.current) afterMapRef.current.getCanvas().style.cursor = '';
  };

  const handleMouseMove = (e: mapboxgl.MapMouseEvent & { features?: mapboxgl.MapboxGeoJSONFeature[] | undefined; }) => {
    setTooltip((prev) => ({ ...prev, x: e.point.x, y: e.point.y }));
  };

  useEffect(() => {
    if (!isBeforeMapStyleLoaded || !isAfterMapStyleLoaded || !beforeMapRef.current || !afterMapRef.current) return;

    commonSiteBoundaryMarkersRef.current.forEach(marker => marker.remove());
    commonSiteBoundaryMarkersRef.current = [];

    const newLayerToggleElements: MapLayerInteractiveElement[] = [];

    if (sites) {
      sites.forEach((site) => {
        if (site.boundary) {
          addSiteBoundary(beforeMapRef.current!, site);
          addSiteBoundary(afterMapRef.current!, site, handleMouseEnter, handleMouseLeave, handleMouseMove);
          newLayerToggleElements.push({
            id: `site-boundary-${site.site_id}`,
            opacity: mapBoxConfig.polygon.defaultPolygonOpacity,
            visibility: true,
            name: site.site_name,
            type: 'fill',
          });
        }

        if (requiredBounrdaryMarker && site.centroid_wkt) {
          addSiteBoundaryMarker(beforeMapRef.current!, site, commonSiteBoundaryMarkersRef.current);
          addSiteBoundaryMarker(afterMapRef.current!, site, commonSiteBoundaryMarkersRef.current);
        }
      });
    }

    setAfterMapLayerToggle(newLayerToggleElements);

    if (
      fitToBoundary &&
      afterMapRef.current &&
      sites &&
      sites.length > 0 &&
      isFirstLoadRef.current
    ) {
      fitMapToBoundaries(afterMapRef.current, sites);
    }
    
    // Mark that the first load has happened
    if (isFirstLoadRef.current) {
      isFirstLoadRef.current = false;
    }
  }, [isBeforeMapStyleLoaded, isAfterMapStyleLoaded, sites, requiredBounrdaryMarker, fitToBoundary]);

  const handleAfterMapLayerOpacityChange = (layer: MapLayerInteractiveElement, newOpacity: number) => {
    const targetLayerId = `${layer.type}-${layer.id}`;
    setAfterMapLayerToggle((prev) =>
      prev?.map((item) => (item.id === layer.id ? { ...item, opacity: newOpacity } : item))
    );
    if (afterMapRef.current?.getLayer(targetLayerId)) {
      afterMapRef.current.setPaintProperty(targetLayerId, `${layer.type}-opacity`, newOpacity);
    }
    if (beforeMapRef.current?.getLayer(targetLayerId)) {
      beforeMapRef.current.setPaintProperty(targetLayerId, `${layer.type}-opacity`, newOpacity);
    }
  };

  const toggleAfterMapLayerVisibility = (layer: MapLayerInteractiveElement) => {
    const isVisible = !layer.visibility;
    const targetLayerId = `${layer.type}-${layer.id}`;
    setAfterMapLayerToggle((prev) =>
      prev?.map((item) => (item.id === layer.id ? { ...item, visibility: isVisible } : item))
    );
    const visibility = isVisible ? 'visible' : 'none';
    if (afterMapRef.current?.getLayer(targetLayerId)) {
      afterMapRef.current.setLayoutProperty(targetLayerId, 'visibility', visibility);
    }
    if (beforeMapRef.current?.getLayer(targetLayerId)) {
      beforeMapRef.current.setLayoutProperty(targetLayerId, 'visibility', visibility);
    }
  };

  const focusMapOnSingleSite = (site: Site) => {
    if (!afterMapRef.current || !site) return;

    if (site.boundary) {
      const tempFeature = new MapboxFeature();
      tempFeature.add(site.boundary, {});
      const geoJsonBoundary = tempFeature.toJSON();

      if (geoJsonBoundary && geoJsonBoundary.features.length > 0) {
        try {
          const calculatedBbox = bbox(geoJsonBoundary);
          afterMapRef.current.fitBounds(calculatedBbox as [number, number, number, number], {
            padding: 50,
            animate: true,
            duration: 1500,
          });
        } catch (e) {
          console.error("Error calculating bounding box for single site:", e);
          if (site.centroid_wkt) {
            const coordinates = MapboxFeature.parsePointToArray(site.centroid_wkt);
            afterMapRef.current.flyTo({ center: coordinates, zoom: 14, animate: true, duration: 1500 });
          }
        }
      }
    } else if (site.centroid_wkt) {
      const coordinates = MapboxFeature.parsePointToArray(site.centroid_wkt);
      afterMapRef.current.flyTo({ center: coordinates, zoom: 14, animate: true, duration: 1500 });
    }
  };

  const handleFocusIconClick = (siteId: string) => {
    const actualSiteId = siteId.replace(/^site-boundary-/, '');
    const siteToFocus = sites?.find(s => s.site_id === actualSiteId);
    if (siteToFocus) {
      focusMapOnSingleSite(siteToFocus);
    }
  };

  return (
    <div style={{ position: 'relative', height: '75vh', width: '100%' }}>
      {isImageryLoading && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.6)',
            color: 'white',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 3,
            flexDirection: 'column',
            textAlign: 'center'
          }}
        >
          <CircularProgress color="inherit" />
          <Typography variant="h6" component="p" sx={{ mt: 2 }}>
            Generating satellite imagery...
          </Typography>
          <Typography variant="body1" component="p">
            This may take a moment.
          </Typography>
        </Box>
      )}
      <div
        ref={compareContainerRef}
        style={{ position: 'absolute', top: 0, bottom: 0, width: '100%', height: '100%' }}
      >
        <div ref={mapContainerBeforeRef} id="before" style={{ position: 'absolute', width: '100%', height: '100%' }} />
        <div ref={mapContainerAfterRef} id="after" style={{ position: 'absolute', width: '100%', height: '100%' }} />
      </div>

      <div style={{ position: 'absolute', top: '10px', right: '10px', zIndex: 2, background: 'white', padding: '10px', borderRadius: '4px', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }}>
        <select id="map-style-selector" onChange={(e) => setMapStyle(e.target.value)} value={mapStyle}>
          <option value="satellite-v9">Satellite</option>
          <option value="streets-v12">Streets</option>
          <option value="light-v11">Light</option>
        </select>
      </div>

      {hasAnySiteWithYearlyImagery && availableYearKeys.length > 0 && (
        <div style={{ position: 'absolute', top: '10px', left: '50%', transform: 'translateX(-50%)', zIndex: 1, background: 'rgba(255,255,255,0.9)', padding: '8px', borderRadius: '4px', display: 'flex', gap: '20px', boxShadow: '0 2px 4px rgba(0,0,0,0.2)' }}>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <select
              id="before-map-tileset-selector"
              value={currentBeforeMapKey}
              onChange={(e) => setCurrentBeforeMapKey(e.target.value)}
              style={{ fontSize: '0.9em', padding: '3px' }}
            >
              {availableYearKeys.map((key) => (
                <option key={key} value={key}>{key}</option>
              ))}
            </select>
          </div>
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <select
              id="after-map-tileset-selector"
              value={currentAfterMapKey}
              onChange={(e) => setCurrentAfterMapKey(e.target.value)}
              style={{ fontSize: '0.9em', padding: '3px' }}
            >
              {availableYearKeys.map((key) => (
                <option key={key} value={key}>{key}</option>
              ))}
            </select>
          </div>
        </div>
      )}

      <div
        className="layer-controls"
        style={{
          position: 'absolute',
          top: '70px',
          right: '10px',
          backgroundColor: 'white',
          padding: '10px',
          border: '1px solid #ccc',
          borderRadius: '4px',
          maxHeight: isSiteListExpanded ? '300px' : '40px',
          overflowY: isSiteListExpanded ? 'auto' : 'hidden',
          transition: 'max-height 0.3s ease',
          zIndex: 1,
          minWidth: '200px',
        }}
      >
        <div
          style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: isSiteListExpanded ? '10px' : '0', cursor: 'pointer' }}
          onClick={() => setIsSiteListExpanded((prev) => !prev)}
        >
          <span>Site Layers</span>
          <button style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px' }}>
            {isSiteListExpanded ? '−' : '+'}
          </button>
        </div>
        {isSiteListExpanded && hasAnySiteWithYearlyImagery && (
          <div style={{ marginBottom: '10px', paddingLeft: '5px', borderBottom: '1px solid #eee', paddingBottom: '10px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <input
                type="checkbox"
                id="checkbox-satellite-imagery"
                checked={showSatelliteImagery}
                onChange={() => setShowSatelliteImagery(prev => !prev)}
              />
              <label htmlFor="checkbox-satellite-imagery" style={{ fontSize: '0.9em' }}>Satellite Imagery</label>
            </div>
          </div>
        )}
        {isSiteListExpanded && afterMapLayerToggle?.map((layer) => (
          <div key={layer.id} style={{ marginBottom: '10px', paddingLeft: '5px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <input
                type="checkbox"
                id={`checkbox-${layer.id}`}
                checked={layer.visibility}
                onChange={() => toggleAfterMapLayerVisibility(layer)}
              />
              <label htmlFor={`checkbox-${layer.id}`} style={{ fontSize: '0.9em' }}>{layer.name}</label>
              <IconButton aria-label="Focus on this site" disabled={!layer.visibility} onClick={() => handleFocusIconClick(layer.id)} >
                <CenterFocusWeakIcon />
              </IconButton>
            </div>
            <input
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={layer.opacity ?? (layer.type === 'fill' ? mapBoxConfig.polygon.defaultPolygonOpacity : mapBoxConfig.raterTile.opacity)}
              onChange={(e) => handleAfterMapLayerOpacityChange(layer, parseFloat(e.target.value))}
              style={{ width: '100%', marginTop: '5px' }}
              disabled={!layer.visibility}
            />
          </div>
        ))}
      </div>

      {tooltip.visible && (
        <div
          style={{
            position: 'absolute',
            left: tooltip.x + 10,
            top: tooltip.y + 10,
            backgroundColor: 'rgba(255, 255, 255, 0.9)',
            padding: '8px',
            border: '1px solid #ccc',
            borderRadius: '4px',
            boxShadow: '0 2px 6px rgba(0, 0, 0, 0.15)',
            zIndex: 1001,
            pointerEvents: 'none',
            fontSize: '0.85em',
          }}
          dangerouslySetInnerHTML={{ __html: tooltip.content }}
        />
      )}
    </div>
  );
};

export default SiteMapWithCompare;
